import { ComplexValue } from 'app/types-shared';
import { FIELD_TYPE } from 'app/infrastructure/simple-form';
import { parseAsDateUTC } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ExtraInfoWireFormat, ExtraInfoType } from 'app/components/access-panel/shared/types';
import { ApiToUse } from 'app/infrastructure/simple-form/types';
import { ExtraInfoValueType, ExtraInfoValue } from './types';

interface ExtraInfoConfiguration {
  toDataMapping: (wireData: ExtraInfoWireFormat[]) => ExtraInfoValueType;
  toWireMapping: (value: ExtraInfoValueType | Date) => Partial<ExtraInfoWireFormat>[];
  formConfiguration: (data: ExtraInfoValue, translation?: any) => {
    type: FIELD_TYPE;
    initialValue: any;
    maxDecimals?: number;
  };
}

export const extraInfoConfig: { [key: string]: ExtraInfoConfiguration } = {
  [ExtraInfoType.text]: {
    toDataMapping: (wireData: ExtraInfoWireFormat[]) => wireData?.length > 0 ? wireData[0].valueString.trim() : undefined,
    toWireMapping: (value: string): Partial<ExtraInfoWireFormat>[] => value ? [{ valueString: value.trim() }] : undefined,
    formConfiguration: (data: ExtraInfoValue, _) => ({
      type: FIELD_TYPE.TEXTAREA,
      initialValue: data.value,
      maxLength: 2000
    })
  },
  [ExtraInfoType.date]: {
    toDataMapping: (wireData: ExtraInfoWireFormat[]) => wireData?.length > 0 ? wireData[0].valueDate : undefined,
    toWireMapping: (value: Date | string): Partial<ExtraInfoWireFormat>[] => value
      ? (value instanceof Date
        ? [{ valueDate: value.toISOString().replace(/\.\d+Z/, '') }]
        : [{ valueDate: value }])
      : undefined,
    formConfiguration: (data: ExtraInfoValue, _) => ({
      type: FIELD_TYPE.DATE_TIME,
      initialValue: data.value instanceof Date
        ? data.value
        : parseAsDateUTC(data.value as string)
    })
  },
  [ExtraInfoType.integer]: {
    toDataMapping: (wireData: ExtraInfoWireFormat[]) => wireData?.length > 0 ? wireData[0].valueInteger : undefined,
    toWireMapping: (value: number): Partial<ExtraInfoWireFormat>[] =>
      isDefinedIncludingZero(value) ? [{ valueInteger: value }] : undefined,
    formConfiguration: (data: ExtraInfoValue, translation: any) => ({
      type: FIELD_TYPE.DECIMAL_NUMBER,
      initialValue: data.value,
      maxDecimals: 0,
      minValue: -2147483648,
      maxValue: 2147483647,
      errorMessage: translation?.must_be_withing_range
    })
  },
  [ExtraInfoType.float]: {
    toDataMapping: (wireData: ExtraInfoWireFormat[]) => wireData?.length > 0 ? wireData[0].valueFloat : undefined,
    toWireMapping: (value: number): Partial<ExtraInfoWireFormat>[] =>
      isDefinedIncludingZero(value) ? [{ valueFloat: value }] : undefined,
    formConfiguration: (data: ExtraInfoValue, translation: any) => ({
      type: FIELD_TYPE.DECIMAL_NUMBER,
      initialValue: data.value,
      maxDecimals: 15,
      errorMessage: translation?.must_be_withing_range
    })
  },
  [ExtraInfoType.singleSelect]: {
    toDataMapping: (wireData: ExtraInfoWireFormat[]) => (wireData?.length > 0 && wireData[0].valueCode
      ? {
        value: wireData[0].valueCode.v,
        description: wireData[0].valueCode.d
      }
      : undefined),
    toWireMapping: (value: ComplexValue<number>): Partial<ExtraInfoWireFormat>[] =>
      (value && value.value ? [{ valueCode: { v: value ? value.value : 0 } }] : undefined),
    formConfiguration: (data: ExtraInfoValue, translation: any) => ({
      type: data.isHierarchy ? FIELD_TYPE.SELECT_SINGLE_HIERARCHY : FIELD_TYPE.SELECT_SINGLE,
      initialValue: data.value,
      placeholder: translation?.search,
      apiToUse: ApiToUse.ADMINAPI,
      descriptionAllLevels: true,
      searchRequired: data.searchRequired,
      categoryId: data.categoryId
    })
  },
  [ExtraInfoType.multiSelect]: {
    toDataMapping: (wireData: ExtraInfoWireFormat[]) => (wireData || [])
      .map(value => value.valueCode
        ? {
          value: value.valueCode.v,
          description: value.valueCode.d
        } : undefined)
      .filter(value => value),
    toWireMapping: (values: ComplexValue<number>[]): Partial<ExtraInfoWireFormat>[] =>
    (values && values.length
      ? values.map(value => ({ valueCode: { v: value ? value.value : 0 } }))
      : undefined),
    formConfiguration: (data: ExtraInfoValue, translation: any) => ({
      type: data?.isHierarchy ? FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY : FIELD_TYPE.SELECT_MULTIPLE,
      initialValue: data.value,
      placeholder: translation?.search,
      apiToUse: ApiToUse.ADMINAPI,
      descriptionAllLevels: true,
      searchRequired: data.searchRequired,
      categoryId: data.categoryId,
      displaySelectAll: true
    })
  },
  [ExtraInfoType.unknown]: {
    toDataMapping: () => undefined,
    toWireMapping: () => undefined,
    formConfiguration: (data: ExtraInfoValue) => undefined
  }
};

export const getConfiguration = (dataType: ExtraInfoType) => extraInfoConfig[dataType] || extraInfoConfig[ExtraInfoType.unknown];
